import {
  CARD_WIDTH,
  CARD_VGAP,
  COLLAPSED_LOOP_HEIGHT,
  COLUMN_GAP,
  COLUMN_MIN_WIDTH,
  CONNECTOR_ZONE,
  LOOP_HEADER_HEIGHT,
  LOOP_PADDING,
  NESTED_FAN_ROW_LEAD,
  WHEN_THEN_SECTION_GAP,
} from "./constants";
import type { LayoutColumn, LayoutItem } from "./types";
import { SECTION_LABEL_ID_PREFIX, TRIGGER_NODE_ID } from "./types";

export const DEFAULT_CARD_HEIGHT = 82;

// eslint-disable-next-line import/no-unused-modules -- consumed by FlowCanvas node components (Task 5+)
export interface NodeGeometry {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Measured node size. `width` is optional: heights drive the vertical
 *  rhythm and always have a default, while widths only refine horizontal
 *  centering (nodes narrower than CARD_WIDTH — e.g. the add-action button —
 *  must be centered by their REAL width or their edge handles skew off the
 *  stack axis). */
export interface NodeSize {
  width?: number;
  height: number;
}

// eslint-disable-next-line import/no-unused-modules -- consumed by FlowCanvas node measurement (Task 6+)
export type NodeSizes = ReadonlyMap<string, NodeSize>;

const heightOf = (sizes: NodeSizes, id: string): number =>
  sizes.get(id)?.height ?? DEFAULT_CARD_HEIGHT;

const widthOf = (sizes: NodeSizes, id: string): number =>
  sizes.get(id)?.width ?? CARD_WIDTH;

/* ---------- pass 1: widths (bottom-up), mirrors CSS width:max-content ---- */

const itemWidth = (item: LayoutItem): number => {
  if (item.kind === "card") return CARD_WIDTH;
  // A collapsed loop keeps the same width as its expanded box (the base
  // CARD_WIDTH + LOOP_PADDING inset) so toggling collapse never jumps the
  // loop's width -- the loop container just reads wider than plain cards in
  // both states.
  if (item.kind === "collapsedLoop") return CARD_WIDTH + 2 * LOOP_PADDING;
  if (item.kind === "group") return stackWidth(item.items) + 2 * LOOP_PADDING;
  return fanWidth(item.columns);
};

const stackWidth = (items: LayoutItem[]): number =>
  items.reduce((max, item) => Math.max(max, itemWidth(item)), CARD_WIDTH);

const columnWidth = (column: LayoutColumn): number =>
  Math.max(COLUMN_MIN_WIDTH, stackWidth(column.items));

const fanWidth = (columns: LayoutColumn[]): number =>
  columns.reduce((sum, c) => sum + columnWidth(c), 0) +
  COLUMN_GAP * Math.max(0, columns.length - 1);

/** Vertical gap before `item`, mirroring legacy Sidebar section spacing. */
const stackGapBefore = (
  item: LayoutItem,
  prev: LayoutItem | undefined,
): number => {
  if (prev === undefined) return 0;
  if (
    prev.kind === "card" &&
    prev.nodeId === TRIGGER_NODE_ID &&
    item.kind === "card"
  ) {
    return WHEN_THEN_SECTION_GAP;
  }
  // The standalone "Then / do this" header hugs the card directly below it
  // (no gap), matching the legacy layout where the header lived inside the
  // first action card with nothing between them.
  if (prev.kind === "card" && prev.nodeId.startsWith(SECTION_LABEL_ID_PREFIX)) {
    return 0;
  }
  return CARD_VGAP;
};

/* ---------- pass 2: positions (top-down) -------------------------------- */

/** Lays out a vertical stack whose horizontal center is `centerX`, starting
 *  at `topY`. Returns the y coordinate just below the stack's last item
 *  (no trailing gap). Writes geometries into `out`. */
const layoutStack = (
  items: LayoutItem[],
  centerX: number,
  topY: number,
  sizes: NodeSizes,
  out: Map<string, NodeGeometry>,
  loopHeaderHeights: ReadonlyMap<string, number>,
): number => {
  let y = topY;
  items.forEach((item, index) => {
    if (index > 0) y += stackGapBefore(item, items[index - 1]);

    if (item.kind === "card") {
      const height = heightOf(sizes, item.nodeId);
      // center by the node's REAL measured width: React Flow anchors edge
      // handles to the actual DOM box, so a narrow node (add button) placed
      // at centerX - CARD_WIDTH / 2 would hang its handles off-axis
      const width = widthOf(sizes, item.nodeId);
      out.set(item.nodeId, {
        x: centerX - width / 2,
        y,
        width,
        height,
      });
      y += height;
      return;
    }

    if (item.kind === "collapsedLoop") {
      const width = itemWidth(item);
      // A wrapped, multi-line group name makes the header taller than the
      // one/two-line COLLAPSED_LOOP_HEIGHT baseline; reserve the measured
      // header height so the name stays inside the border (BUG-21207).
      const height = Math.max(
        COLLAPSED_LOOP_HEIGHT,
        loopHeaderHeights.get(item.nodeId) ?? 0,
      );
      out.set(item.nodeId, {
        x: centerX - width / 2,
        y,
        width,
        height,
      });
      y += height;
      return;
    }

    if (item.kind === "group") {
      const width = itemWidth(item);
      // The header is pinned above the first child; grow its reserved zone
      // with the measured (possibly wrapped) header so it never overlaps the
      // first action card (BUG-21207).
      const headerZone = Math.max(
        LOOP_HEADER_HEIGHT,
        loopHeaderHeights.get(item.nodeId) ?? 0,
      );
      // children get coordinates relative to the group node (RF parentId)
      const innerBottom = layoutStack(
        item.items,
        width / 2,
        headerZone + LOOP_PADDING,
        sizes,
        out,
        loopHeaderHeights,
      );
      const height = innerBottom + LOOP_PADDING;
      out.set(item.nodeId, { x: centerX - width / 2, y, width, height });
      y += height;
      return;
    }

    // fan
    const rowTop =
      item.blockNodeId !== undefined
        ? (() => {
            const blockHeight = heightOf(sizes, item.blockNodeId);
            const blockWidth = widthOf(sizes, item.blockNodeId);
            out.set(item.blockNodeId, {
              x: centerX - blockWidth / 2,
              y,
              width: blockWidth,
              height: blockHeight,
            });
            return y + blockHeight + CONNECTOR_ZONE;
          })()
        : y + NESTED_FAN_ROW_LEAD;
    const totalWidth = fanWidth(item.columns);
    let cursorX = centerX - totalWidth / 2;
    let rowBottom = rowTop;
    item.columns.forEach((column) => {
      const width = columnWidth(column);
      const colCenterX = cursorX + width / 2;
      const headerHeight = heightOf(sizes, column.headerNodeId);
      const headerWidth = widthOf(sizes, column.headerNodeId);
      out.set(column.headerNodeId, {
        x: colCenterX - headerWidth / 2,
        y: rowTop,
        width: headerWidth,
        height: headerHeight,
      });
      const columnBottom = layoutStack(
        column.items,
        colCenterX,
        rowTop + headerHeight + (column.items.length > 0 ? CARD_VGAP : 0),
        sizes,
        out,
        loopHeaderHeights,
      );
      rowBottom = Math.max(rowBottom, columnBottom);
      cursorX += width + COLUMN_GAP;
    });
    y = rowBottom;
  });
  return y;
};

export const layoutGraph = (
  items: LayoutItem[],
  sizes: NodeSizes,
  /** Measured header heights keyed by loop-group node id. A loop's header is
   *  absolutely positioned (it never grows its own box), so the layout engine
   *  must reserve its real height — otherwise a wrapped, multi-line group name
   *  overflows the collapsed card / overlaps the first expanded child. */
  loopHeaderHeights: ReadonlyMap<string, number> = new Map(),
): Map<string, NodeGeometry> => {
  const out = new Map<string, NodeGeometry>();
  layoutStack(items, 0, 0, sizes, out, loopHeaderHeights);
  return out;
};
